import {
  Avatar,
  Box,
  Divider,
  Drawer,
  GridLegacy,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode, createContext, useState, Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import ExtendedTabs, {
  ExtendedTabOption,
} from 'ui-component/extended/ExtendedTabs';
import { SxProps, useTheme } from '@mui/material/styles';
import { actionButtonSpacing, getHeaderTabSx } from 'constants/themeConstants';
import SidebarInfoSection, {
  SidebarInfoSectionProps,
} from 'ui-component/DetailLayout/SidebarComponents/SidebarInfoSection';
import _ from 'lodash';
import {
  IconArrowLeft,
  IconFileExport,
  IconLayoutBottombarCollapse,
  IconLayoutBottombarExpand,
  IconLayoutNavbarCollapse,
  IconLayoutNavbarExpand,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
  IconPaperclip,
  IconPlug,
  IconShare,
  IconTag,
} from '@tabler/icons-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { ALL, GenericResource } from 'types/api';
import { ALL_VIEWS } from 'constants/appConstants';
import { ALL_RECORD_TYPE_IDS, ALL_RECORD_TYPES } from 'constants/recordTypes';
import { appPermissionAccessLevels, RecordType } from 'types/apps';
import SidebarApproveButtonClientV2 from 'ui-component/DetailLayout/SidebarComponents/SidebarApproveButtonClientV2';
import SidebarRejectButtonClientV2 from 'ui-component/DetailLayout/SidebarComponents/SidebarRejectButtonClientV2';
import SidebarDeleteButtonClientV2 from 'ui-component/DetailLayout/SidebarComponents/SidebarDeleteButtonClientV2';
import SidebarArchiveButtonClientV2 from 'ui-component/DetailLayout/SidebarComponents/SidebarArchiveButtonClientV2';
import SidebarCustomProperties from 'ui-component/DetailLayout/SidebarComponents/SidebarCustomProperties';
import { InfoSectionItemFormatTypes } from 'ui-component/InfoSectionItem';
import EditInline from 'ui-component/EditInline';
import HookFormTagsAutocomplete from 'ui-component/HookFormComponents/HookFormTagsAutocomplete';
import ExternallySyncedInfoSection from 'ui-component/ExternallySyncedInfoSection';
import { formatToDateTime } from 'utils/dates';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { DateTimeString, FeatureFlags } from 'types';
import { Tag } from 'types/clientV2/tag';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useGetStockDocumentRelatedObjectsQuery } from 'store/slices/clientV2/stockDocuments';
import {
  StockDocumentRelatedObjectSchema,
  StockDocumentRelatedObjectWithStockDocument,
} from 'types/clientV2/stockDocuments';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { DatagridFilterOperatorsValues } from 'types/datagrid';
import { useFeature } from '@growthbook/growthbook-react';
import { DocumentTemplate, RelatedDocumentRecord } from 'types/inventory';
import GenerateDocumentButton from 'ui-component/StockDocuments/GenerateDocumentButton';
import InlineUploadDocumentsDropZone from 'ui-component/StockDocuments/UploadDocuments/InlineUploadDocumentsDropZone';
import { useDialogManager } from 'hooks/useDialogManager';
import StockDocumentsDialog from 'ui-component/StockDocuments/StockDocumentsDialog';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { useDispatch, useSelector } from 'store';
import SidebarActionButton from 'ui-component/DetailLayout/SidebarComponents/SidebarActionButton';
import { copyClipboard, createSxWithoutUndefined } from 'utils/functions';
import SkeletonTab from 'ui-component/SkeletonTab';
import VeltPresenceSidebarInfoSection from 'ui-component/Velt/VeltPresenceSidebarInfoSection';
import VeltCommentsSidebarInfoSection from 'ui-component/Velt/VeltCommentsSidebarInfoSection';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import { VeltCommentsSidebar } from '@veltdev/react';
import VeltSidebarSubscribeActionButton from 'ui-component/Velt/VeltSidebarSubscribeActionButton';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import CofactrIcon from 'ui-component/CofactrIcon';
import PageTitle from 'ui-component/PageTitle';
import { setDetailDrawer } from 'store/slices/detailDrawer';
import useCreateLinkToRecord from 'hooks/useCreateLinkToRecord';
import { PizzaTrackerStep } from 'types/pizzaGraph';
import PizzaTracker from 'ui-component/PizzaGraph/PizzaTracker';

export enum DETAIL_VIEW_DIALOGS {
  GENERATED_DOCUMENTS = 'GENERATED_DOCUMENTS',
  ATTACHMENTS = 'ATTACHMENTS',
}

export const DrawerContext = createContext({
  open: false,
  commentMode: false,
  setOpen: (open: boolean) => {},
  setCommentMode: (commentMode: boolean) => {},
});

export interface DetailLayoutProps<T extends GenericResource> {
  headerSection?: ReactNode;
  headerBottomContent?: ReactNode;
  mainCardContent?: ReactNode;
  footerSection?: ReactNode;
  tabOptions?: ExtendedTabOption[];
  sidebarActionButtons?: ReactNode[];
  sidebarInfoSections?: SidebarInfoSectionProps[];
  additionalSidebarSections?: ReactNode[];
  tabTrackingName?: string;
  record?: T | null;
  recordType?: ALL_RECORD_TYPE_IDS | null;
  isLoadingRecord?: boolean;
  documentGenerationProps?: {
    defaultDocumentTemplateId?: DocumentTemplate['id'] | null;
    additionalGenerateProps?: object;
  };
  isLoadingTabOptions?: boolean;
  relatedDocumentRecords?: RelatedDocumentRecord[];
  managedByCofactrApprovedText?: string;
  managedByCofactrUnapprovedText?: string;
  isApproved?: boolean;
  defaultTab?: string;
  overrideCanApprove?: boolean;
  pizzaTrackerSteps?: PizzaTrackerStep[];
  hideUserInfoSections?: boolean;
  hidePizzaTracker?: boolean;
}

const DetailLayout = <T extends GenericResource>({
  headerSection,
  headerBottomContent,
  footerSection,
  mainCardContent,
  tabOptions,
  sidebarActionButtons = [],
  sidebarInfoSections = [],
  additionalSidebarSections = [],
  tabTrackingName,
  record,
  recordType,
  isLoadingRecord = false,
  documentGenerationProps,
  isLoadingTabOptions = false,
  relatedDocumentRecords = [],
  managedByCofactrApprovedText,
  managedByCofactrUnapprovedText,
  isApproved = false,
  defaultTab,
  overrideCanApprove,
  pizzaTrackerSteps,
  hideUserInfoSections = false,
  hidePizzaTracker = false,
}: DetailLayoutProps<T>) => {
  const navigate = useNavigate();
  const tempVelt = useFeature(FeatureFlags.tempVelt).on;
  const theme = useTheme();
  const { open } = useSelector((state) => state.detailDrawer);
  const dispatch = useDispatch();
  const [commentMode, setCommentMode] = useState(false);
  const [headerOpen, setHeaderOpen] = useState(true);
  const [footerOpen, setFooterOpen] = useState(true);

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  );

  const { isCofactr } = useIsCofactr();
  const { isPermissionDisabled } = useAppAccessContext();
  const { openDialog, closeDialog, isDialogOpen } = useDialogManager(
    Object.values(DETAIL_VIEW_DIALOGS)
  );

  const { createLinkWithActiveOrgId } = useCreateLinkToRecord();

  const setOpen = (openState: boolean) => {
    dispatch(setDetailDrawer(openState));
  };

  const handleDrawerOpen = () => {
    setOpen(true);
    setCommentMode(false);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setCommentMode(false);
  };

  const recordTypeDefinition = recordType
    ? (ALL_RECORD_TYPES[recordType] as unknown as RecordType<T>)
    : null;

  const recordName =
    recordTypeDefinition?.nameGetter && record
      ? recordTypeDefinition.nameGetter(record)
      : undefined;

  const trackerSteps =
    pizzaTrackerSteps ??
    (record ? recordTypeDefinition?.statusGetter?.(record) : undefined);

  const canApprove = useMemo(() => {
    if (overrideCanApprove !== undefined) return overrideCanApprove;

    if (!recordTypeDefinition?.requireAdminApprovals) return true;

    return !isPermissionDisabled({
      app: recordTypeDefinition.app,
      accessLevel: appPermissionAccessLevels.admin,
    });
  }, [
    recordTypeDefinition?.requireAdminApprovals,
    recordTypeDefinition?.app,
    overrideCanApprove,
    isPermissionDisabled,
  ]);

  const recordExternallySynced = _.get(record, 'externallySynced', false);
  const recordTags = record?.tags ? (_.get(record, 'tags', []) as Tag[]) : null;

  const isExternallySynced: boolean = Boolean(
    recordTypeDefinition?.externallySyncableRecord && recordExternallySynced
  );

  const { data: stockDocuments, isLoading: isLoadingStockDocuments } =
    useServerSideQuery<
      StockDocumentRelatedObjectSchema,
      StockDocumentRelatedObjectWithStockDocument
    >(
      useGetStockDocumentRelatedObjectsQuery,
      record?.id && recordType
        ? {
            filters: [
              {
                field: 'relatedObjectId',
                operator: DatagridFilterOperatorsValues.isAnyOf,
                value: [
                  record.id,
                  ...relatedDocumentRecords
                    .filter((d) => !!d.id)
                    .slice(0, 29)
                    .map((d) => d.id),
                ], // we limit to 30 related documents to avoid hitting the URI length limit
              },
            ],
            schema: [
              ALL,
              {
                stockDocument: [],
              },
            ],
            blended: true,
          }
        : skipToken
    );

  const relatedStockDocuments = _.chain(stockDocuments?.data || [])
    .filter((d) => d.stockDocument.createdFromObjectId !== record?.id)
    .uniqBy((d) => d.stockDocument.id)
    .value();
  const generatedStockDocuments: StockDocumentRelatedObjectWithStockDocument[] =
    _.chain(stockDocuments?.data || [])
      .filter((d) => d.stockDocument.createdFromObjectId === record?.id)
      .uniqBy((d) => d.stockDocument.id)
      .value();

  const allSidebarInfoSections = useMemo(
    () => [
      ...(record &&
      recordTypeDefinition?.flagshipModel &&
      recordTypeDefinition.generatePdfMutation
        ? ([
            {
              caption: 'Generate Documents',
              isLoading: isLoadingRecord || isLoadingStockDocuments,
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.documents,
              documentsValue: generatedStockDocuments,
              count: generatedStockDocuments?.length || 0,
              Icon: IconFileExport,
              onIconClick: () => {
                setSelectedDocumentId(null);
                openDialog(DETAIL_VIEW_DIALOGS.GENERATED_DOCUMENTS);
              },
              children: recordType ? (
                <GenerateDocumentButton
                  size="small"
                  recordType={recordType}
                  defaultDocumentTemplateId={
                    documentGenerationProps?.defaultDocumentTemplateId ||
                    undefined
                  }
                  isLoading={isLoadingRecord}
                  boxSx={
                    generatedStockDocuments?.length || isLoadingRecord
                      ? { mt: actionButtonSpacing }
                      : undefined
                  }
                  additionalGenerateProps={
                    documentGenerationProps?.additionalGenerateProps
                  }
                />
              ) : undefined,
              alwaysVisible: true,
              collapsedTooltip: _.chain(generatedStockDocuments)
                .map((doc) => doc.stockDocument.name)
                .join(', ')
                .value(),
              documentsOnClick: (id) => {
                setSelectedDocumentId(id);
                openDialog(DETAIL_VIEW_DIALOGS.GENERATED_DOCUMENTS);
              },
            },
          ] as SidebarInfoSectionProps[])
        : []),
      ...(record && recordTypeDefinition?.flagshipModel
        ? ([
            {
              caption: 'Attachments',
              isLoading: isLoadingRecord || isLoadingStockDocuments,
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.documents,
              documentsValue: relatedStockDocuments,
              count: relatedStockDocuments.length,
              Icon: IconPaperclip,
              onIconClick: () => {
                setSelectedDocumentId(null);
                openDialog(DETAIL_VIEW_DIALOGS.ATTACHMENTS);
              },
              children: recordType ? (
                <InlineUploadDocumentsDropZone<T>
                  record={record}
                  recordType={recordType}
                  isLoading={isLoadingRecord}
                  boxSx={
                    relatedStockDocuments.length || isLoadingRecord
                      ? { mt: actionButtonSpacing }
                      : undefined
                  }
                />
              ) : undefined,
              alwaysVisible: true,
              collapsedTooltip: _.chain(relatedStockDocuments)
                .map((doc) => doc.stockDocument.name)
                .join(', ')
                .value(),
              documentsOnClick: (id) => {
                setSelectedDocumentId(id);
                openDialog(DETAIL_VIEW_DIALOGS.ATTACHMENTS);
              },
            },
          ] as SidebarInfoSectionProps[])
        : []),
      ...(isExternallySynced
        ? [
            {
              caption: 'Integration Source',
              isLoading: isLoadingRecord,
              placeholder: ' ',
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.children,
              textValue: record?.dataSource,
              Icon: IconPlug,
              alwaysVisible: true,
              children: <ExternallySyncedInfoSection record={record as T} />,
              collapsedTooltip: `Synced from integration at ${
                record?.dataSourceSyncedAt
                  ? formatToDateTime(
                      record?.dataSourceSyncedAt as DateTimeString
                    )
                  : 'Unknown'
              }`,
            } as SidebarInfoSectionProps,
          ]
        : []),
      ...(recordTypeDefinition?.tagRecord && recordTypeDefinition.updateMutation
        ? [
            {
              caption: 'Tags',
              isLoading: isLoadingRecord,
              formatType: InfoSectionItemFormatTypes.tags,
              tagsValue: recordTags,
              count: recordTags?.length,
              Icon: IconTag,
              disableEdit: isExternallySynced,
              collapsedTooltip: _.chain(recordTags)
                .map((tag) => tag.name)
                .join(', ')
                .value(),
              editInline: (
                <EditInline
                  useMutation={
                    recordTypeDefinition.updateMutation as UseMutation<any>
                  }
                  name="tags"
                  label="Tags"
                  value={recordTags?.map((tag) => tag.id)}
                  id={record?.id || ''}
                  permissionScope={{
                    app: recordTypeDefinition.app,
                    accessLevel: appPermissionAccessLevels.edit,
                  }}
                  submitAsClientV2Api
                >
                  <HookFormTagsAutocomplete
                    tagModel={recordTypeDefinition?.flagshipModel}
                    disableRequiredProps
                    isInlineEdit
                  />
                </EditInline>
              ),
            } as SidebarInfoSectionProps,
          ]
        : []),
      ...sidebarInfoSections,
      ...(record?.createdAt && !hideUserInfoSections
        ? [
            {
              caption: 'Created',
              isLoading: isLoadingRecord,
              placeholder: ' ',
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.user,
              userValue: record?.createdBy,
              dateValue: record?.createdAt,
              useInfoSectionAutomationOption: true,
              visible: Boolean(record?.createdAt),
            } as SidebarInfoSectionProps,
          ]
        : []),
      ...(record?.approvedAt && !hideUserInfoSections
        ? [
            {
              caption: 'Approved',
              isLoading: isLoadingRecord,
              placeholder: ' ',
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.user,
              userValue: record?.approvedBy,
              dateValue: record?.approvedAt,
              useInfoSectionAutomationOption: true,
              visible: Boolean(record?.approvedAt),
            } as SidebarInfoSectionProps,
          ]
        : []),
      ...(record?.voidedAt && !hideUserInfoSections
        ? [
            {
              caption: 'Rejected',
              isLoading: isLoadingRecord,
              placeholder: ' ',
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.user,
              userValue: record?.voidedBy,
              dateValue: record?.voidedAt,
              useInfoSectionAutomationOption: true,
              visible: Boolean(record?.voidedAt),
            } as SidebarInfoSectionProps,
          ]
        : []),
      ...(record?.archivedAt && !hideUserInfoSections
        ? [
            {
              caption: 'Archived',
              isLoading: isLoadingRecord,
              placeholder: ' ',
              clipboard: false,
              formatType: InfoSectionItemFormatTypes.user,
              userValue: record?.archivedBy,
              dateValue: record?.archivedAt,
              useInfoSectionAutomationOption: true,
              visible: Boolean(record?.archivedAt),
            } as SidebarInfoSectionProps,
          ]
        : []),
    ],
    [
      isExternallySynced,
      isLoadingRecord,
      record,
      recordTypeDefinition?.tagRecord,
      recordTypeDefinition?.updateMutation,
      recordTypeDefinition?.app,
      recordTypeDefinition?.flagshipModel,
      recordTypeDefinition?.generatePdfMutation,
      recordTags,
      isLoadingStockDocuments,
      generatedStockDocuments,
      recordType,
      documentGenerationProps?.defaultDocumentTemplateId,
      documentGenerationProps?.additionalGenerateProps,
      relatedStockDocuments,
      sidebarInfoSections,
      openDialog,
    ]
  );

  const sidebarItemsWhenCollapsed = _.filter(
    allSidebarInfoSections,
    (section) => !!section.Icon && (!!section.count || !!section.alwaysVisible)
  );

  const showInfoSectionDivider = useMemo(
    () =>
      open
        ? allSidebarInfoSections.length > 0 ||
          additionalSidebarSections.length > 0 ||
          recordTypeDefinition?.customPropertyModel ||
          recordTypeDefinition?.tagRecord ||
          tempVelt
        : sidebarItemsWhenCollapsed.length > 0 ||
          (recordTypeDefinition?.tagRecord &&
            recordTags &&
            recordTags.length > 0) ||
          tempVelt,
    [
      open,
      sidebarItemsWhenCollapsed,
      allSidebarInfoSections,
      additionalSidebarSections,
      recordTypeDefinition?.customPropertyModel,
      recordTypeDefinition?.tagRecord,
      tempVelt,
      recordTags,
    ]
  );

  const visibleSidebarInfoSections = allSidebarInfoSections.filter(
    (section) => section.visible !== false
  );

  const collapsedDrawerWidth = 69;
  const drawerWidth = commentMode ? 380 : 260;

  const managedByCofactrText =
    record?.approvedAt || isApproved
      ? managedByCofactrApprovedText
      : managedByCofactrUnapprovedText;
  const approved = !!(record?.approvedAt || isApproved);

  const showPizzaTracker = trackerSteps && !hidePizzaTracker;

  const headerTabSx = getHeaderTabSx(theme);

  return (
    <>
      {recordName && <PageTitle customTitle={recordName} />}
      <GridLegacy
        container
        direction="column"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          width: '100%',
          height: 'calc(100% + 40px)',
          mt: '-20px',
          mb: '-20px',
          py: 0,
          pr: `${open ? drawerWidth : collapsedDrawerWidth}px`,
        }}
      >
        {headerSection && (
          <GridLegacy item xs="auto">
            <Stack
              direction="column"
              spacing={0}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              {headerOpen && (
                <MainCard
                  sx={
                    showPizzaTracker || managedByCofactrText
                      ? {
                          width: '100%',
                          mt: '40px',
                          pt: '20px',
                          position: 'relative',
                          overflow: 'visible',
                        }
                      : {
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                          width: '100%',
                        }
                  }
                  bottomContent={headerBottomContent}
                  highlight={approved}
                >
                  {/* PizzaTracker header */}
                  {showPizzaTracker && headerOpen && (
                    <Box
                      sx={{
                        ...headerTabSx,
                        left: '16px',
                        backgroundColor: 'white',
                        borderColor: approved
                          ? theme.palette.success.main
                          : theme.palette.grey[300],
                      }}
                    >
                      {isLoadingRecord ? (
                        <Skeleton
                          variant="rounded"
                          width={280}
                          height={32}
                          sx={{ borderRadius: '100px' }}
                        />
                      ) : (
                        <PizzaTracker steps={trackerSteps} />
                      )}
                    </Box>
                  )}
                  {/* Managed By Cofactr header */}
                  {!!managedByCofactrText && (
                    <Tooltip title={managedByCofactrText}>
                      <Box
                        sx={
                          {
                            ...headerTabSx,
                            right: '16px',
                            backgroundColor: approved
                              ? theme.palette.brand.moss
                              : 'white',
                            borderColor: approved
                              ? theme.palette.success.main
                              : theme.palette.grey[300],
                          } as SxProps<Theme>
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            height: '100%',
                            mx: 1,
                          }}
                        >
                          <CofactrIcon size={24} />
                          <Typography
                            variant="h5"
                            color={approved ? 'primary' : undefined}
                          >
                            Managed by Cofactr
                          </Typography>
                        </Stack>
                      </Box>
                    </Tooltip>
                  )}
                  {headerSection}
                </MainCard>
              )}
              <Avatar
                variant="rounded"
                sx={createSxWithoutUndefined({
                  ...theme.typography.commonAvatar,
                  ...theme.typography.mediumAvatar,
                  overflow: 'hidden',
                  transition: 'all .2s ease-in-out',
                  background: theme.palette.brand.moss,
                  color: theme.palette.brand.steel,
                  borderTopLeftRadius: headerOpen ? undefined : 0,
                  borderTopRightRadius: headerOpen ? undefined : 0,
                  marginTop: headerOpen ? '-17px' : 0,
                  '&:hover': {
                    background: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                  },
                })}
                onClick={() =>
                  setHeaderOpen((prevHeaderOpen) => !prevHeaderOpen)
                }
                color="inherit"
              >
                {headerOpen ? (
                  <IconLayoutNavbarCollapse stroke={1.5} size="1.3rem" />
                ) : (
                  <IconLayoutNavbarExpand stroke={1.5} size="1.3rem" />
                )}
              </Avatar>
            </Stack>
          </GridLegacy>
        )}
        {tabOptions && tabOptions.length > 0 && (
          <GridLegacy
            item
            xs
            sx={{
              mt: headerSection ? '8px' : '0px',
              mb: footerSection ? '8px' : '0px',
            }}
          >
            <MainCard
              contentSX={{ p: 0, '&:last-child': { p: 0 } }}
              highlight={approved}
            >
              {isLoadingTabOptions && <SkeletonTab />}
              {!isLoadingTabOptions && (
                <ExtendedTabs
                  tabOptions={tabOptions}
                  queryParam="tab"
                  trackingName={tabTrackingName}
                  defaultTab={defaultTab}
                />
              )}
            </MainCard>
          </GridLegacy>
        )}
        {mainCardContent && (
          <GridLegacy
            item
            xs
            sx={{
              mt: headerSection ? '8px' : '0px',
              mb: footerSection ? '8px' : '0px',
            }}
          >
            <MainCard
              contentSX={{ p: 0, '&:last-child': { p: 0 } }}
              highlight={approved}
            >
              {mainCardContent}
            </MainCard>
          </GridLegacy>
        )}
        {footerSection && (
          <GridLegacy item xs="auto">
            <Stack
              direction="column"
              spacing={0}
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Avatar
                variant="rounded"
                sx={createSxWithoutUndefined({
                  ...theme.typography.commonAvatar,
                  ...theme.typography.mediumAvatar,
                  overflow: 'hidden',
                  transition: 'all .2s ease-in-out',
                  background: theme.palette.brand.moss,
                  color: theme.palette.brand.steel,
                  borderBottomLeftRadius: footerOpen ? undefined : 0,
                  borderBottomRightRadius: footerOpen ? undefined : 0,
                  marginBottom: footerOpen ? '-17px' : 0,
                  '&:hover': {
                    background: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                  },
                })}
                onClick={() =>
                  setFooterOpen((prevFooterOpen) => !prevFooterOpen)
                }
                color="inherit"
              >
                {footerOpen ? (
                  <IconLayoutBottombarCollapse stroke={1.5} size="1.3rem" />
                ) : (
                  <IconLayoutBottombarExpand stroke={1.5} size="1.3rem" />
                )}
              </Avatar>
              {footerOpen && (
                <MainCard
                  sx={{
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    width: '100%',
                  }}
                  highlight={approved}
                >
                  {footerSection}
                </MainCard>
              )}
            </Stack>
          </GridLegacy>
        )}
      </GridLegacy>
      <Drawer
        variant="permanent"
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : collapsedDrawerWidth,
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRight: 'none',
            height: '100%',
            overflowX: 'hidden',
          },
        }}
        ModalProps={{ keepMounted: true }}
        color="inherit"
      >
        <Box
          sx={{
            position: 'fixed',
            top: '16px',
            right: `${(open ? drawerWidth : collapsedDrawerWidth) - 34 / 2}px`,
            zIndex: 100000,
          }}
        >
          <Avatar
            variant="rounded"
            sx={createSxWithoutUndefined({
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              overflow: 'hidden',
              transition: 'all .2s ease-in-out',
              background: theme.palette.brand.moss,
              color: theme.palette.brand.steel,
              '&:hover': {
                background: theme.palette.primary.dark,
                color: theme.palette.common.white,
              },
            })}
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            color="inherit"
          >
            {open ? (
              <IconLayoutSidebarRightCollapse stroke={1.5} size="1.3rem" />
            ) : (
              <IconLayoutSidebarRightExpand stroke={1.5} size="1.3rem" />
            )}
          </Avatar>
        </Box>
        <DrawerContext.Provider
          value={{ open, commentMode, setOpen, setCommentMode }}
        >
          {commentMode ? (
            <Box
              sx={{
                my: 1,
                width: `${drawerWidth}px`,
                height: 'calc(100% - 16px)',
              }}
            >
              <Box sx={{ width: '100%', height: '46px', pl: 2 }}>
                <SegmentButton
                  color="primary"
                  onClick={() => setCommentMode(false)}
                  startIcon={<IconArrowLeft />}
                  sx={{ mx: 1, mt: 1 }}
                >
                  Back
                </SegmentButton>
              </Box>
              <Box sx={{ width: '100%', height: 'calc(100% - 46px)' }}>
                <VeltCommentsSidebar
                  embedMode
                  shadowDom={false}
                  pageMode
                  pageModeComposerVariant="pageModeDialogInSidebar"
                  sortData="asc"
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ height: '100%' }}>
              <PerfectScrollbar component="div">
                <GridLegacy
                  container
                  direction="column"
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'stretch',
                    mb: actionButtonSpacing,
                  }}
                >
                  <GridLegacy item xs="auto">
                    <Box sx={{ width: '100%', height: '36px' }} />
                  </GridLegacy>
                  <GridLegacy item xs="auto" sx={{ px: '16px' }}>
                    <Stack
                      direction="column"
                      spacing={actionButtonSpacing}
                      sx={{
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                      }}
                    >
                      {recordTypeDefinition?.approveMutation &&
                        recordTypeDefinition?.unapproveMutation &&
                        record?.id && (
                          <SidebarApproveButtonClientV2<
                            T,
                            typeof recordTypeDefinition.approveMutation,
                            typeof recordTypeDefinition.unapproveMutation
                          >
                            recordName={recordTypeDefinition.singularName}
                            approved={record?.approvedAt !== null}
                            idToUpdate={record?.id}
                            useClientApproveMutation={
                              recordTypeDefinition.approveMutation
                            }
                            useClientUnapproveMutation={
                              recordTypeDefinition.unapproveMutation
                            }
                            permissionScope={{
                              app: recordTypeDefinition.app,
                              accessLevel: appPermissionAccessLevels.edit,
                            }}
                            disabled={isExternallySynced || !canApprove}
                          />
                        )}
                      {recordTypeDefinition?.approveMutation &&
                        !recordTypeDefinition?.unapproveMutation &&
                        record?.id && (
                          <SidebarApproveButtonClientV2<
                            T,
                            typeof recordTypeDefinition.approveMutation,
                            typeof recordTypeDefinition.approveMutation
                          >
                            recordName={recordTypeDefinition.singularName}
                            approved={record?.approvedAt !== null}
                            idToUpdate={record?.id}
                            useClientApproveMutation={
                              recordTypeDefinition.approveMutation
                            }
                            useClientUnapproveMutation={
                              recordTypeDefinition.approveMutation
                            }
                            permissionScope={{
                              app: recordTypeDefinition.app,
                              accessLevel: appPermissionAccessLevels.edit,
                            }}
                            disabled={
                              isExternallySynced ||
                              !canApprove ||
                              record?.approvedAt !== null
                            }
                          />
                        )}
                      {recordTypeDefinition?.voidMutation &&
                        recordTypeDefinition?.unvoidMutation &&
                        record?.id && (
                          <SidebarRejectButtonClientV2<T>
                            recordName={recordTypeDefinition.singularName}
                            voided={record?.voidedAt !== null}
                            idToUpdate={record?.id}
                            useClientVoidMutation={
                              recordTypeDefinition.voidMutation
                            }
                            useClientUnvoidMutation={
                              recordTypeDefinition.unvoidMutation
                            }
                            permissionScope={{
                              app: recordTypeDefinition.app,
                              accessLevel: appPermissionAccessLevels.edit,
                            }}
                            disabled={isExternallySynced}
                          />
                        )}
                      {sidebarActionButtons.map((button, index) => (
                        <Box key={index}>{button}</Box>
                      ))}
                      {isCofactr && record && (
                        <SidebarActionButton
                          Icon={IconShare}
                          label="Share Link"
                          onClick={() => {
                            copyClipboard(
                              createLinkWithActiveOrgId(window.location.href)
                            );
                          }}
                        />
                      )}
                      {recordTypeDefinition?.archiveMutation &&
                        recordTypeDefinition?.unarchiveMutation &&
                        record?.id && (
                          <SidebarArchiveButtonClientV2<T>
                            recordName={recordTypeDefinition.singularName}
                            archived={record?.archivedAt !== null}
                            idToUpdate={record?.id}
                            useClientArchiveMutation={
                              recordTypeDefinition.archiveMutation
                            }
                            useClientUnarchiveMutation={
                              recordTypeDefinition.unarchiveMutation
                            }
                            permissionScope={{
                              app: recordTypeDefinition.app,
                              accessLevel: appPermissionAccessLevels.edit,
                            }}
                            disabled={isExternallySynced}
                          />
                        )}
                      {recordTypeDefinition?.deleteMutation && record?.id && (
                        <SidebarDeleteButtonClientV2<T>
                          recordName={recordTypeDefinition.singularName}
                          idToUpdate={record?.id}
                          useClientDeleteMutation={
                            recordTypeDefinition.deleteMutation
                          }
                          permissionScope={{
                            app: recordTypeDefinition.app,
                            accessLevel: appPermissionAccessLevels.edit,
                          }}
                          onSuccess={() => {
                            if (recordTypeDefinition.view) {
                              navigate(
                                ALL_VIEWS[recordTypeDefinition.view]
                                  .path as string
                              );
                            }
                          }}
                        />
                      )}
                      {tempVelt && record && recordType && (
                        <VeltSidebarSubscribeActionButton />
                      )}
                    </Stack>
                  </GridLegacy>
                  {showInfoSectionDivider && (
                    <Divider sx={{ mt: 1, mb: 1 }} flexItem />
                  )}
                  <GridLegacy item xs>
                    <Stack
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="stretch"
                      spacing={actionButtonSpacing}
                      sx={{ mx: '16px', mt: actionButtonSpacing }}
                    >
                      {tempVelt && record && recordType && (
                        <>
                          <VeltPresenceSidebarInfoSection<T>
                            record={record}
                            recordType={recordType}
                          />
                          <VeltCommentsSidebarInfoSection<T>
                            record={record}
                            recordType={recordType}
                          />
                          <Divider flexItem />
                        </>
                      )}
                      {open &&
                        _.map(visibleSidebarInfoSections, (section, index) => (
                          <Fragment key={`${section.caption}-${index}`}>
                            <SidebarInfoSection {...section} />
                            {(section.visible ||
                              section.visible === undefined) && (
                              <Divider flexItem />
                            )}
                          </Fragment>
                        ))}
                      {recordTypeDefinition?.customPropertyModel &&
                      record?.customProperties &&
                      recordTypeDefinition.updateMutation ? (
                        <SidebarCustomProperties<T>
                          useMutation={recordTypeDefinition.updateMutation}
                          record={record}
                          model={recordTypeDefinition.customPropertyModel}
                          isLoadingRecord={isLoadingRecord}
                          idProp="id"
                          submitAsClientV2Api
                          disableEdit={isExternallySynced}
                        />
                      ) : null}
                      {open &&
                        _.map(additionalSidebarSections, (section, index) => (
                          <Fragment key={index}>{section}</Fragment>
                        ))}
                      {!open &&
                        sidebarItemsWhenCollapsed.length > 0 &&
                        _.map(sidebarItemsWhenCollapsed, (section, index) => (
                          <SidebarInfoSection
                            {...section}
                            key={`${section.caption}-${index}`}
                          />
                        ))}
                    </Stack>
                  </GridLegacy>
                </GridLegacy>
              </PerfectScrollbar>
            </Box>
          )}
        </DrawerContext.Provider>
      </Drawer>
      {record && recordType && (
        <StockDocumentsDialog
          open={isDialogOpen(DETAIL_VIEW_DIALOGS.GENERATED_DOCUMENTS)}
          onClose={closeDialog}
          selectedDocumentId={selectedDocumentId}
          setSelectedDocumentId={setSelectedDocumentId}
          title="Generate Documents"
          documents={generatedStockDocuments}
          disableDelete
        >
          <GenerateDocumentButton
            size="small"
            recordType={recordType}
            defaultDocumentTemplateId={
              documentGenerationProps?.defaultDocumentTemplateId || undefined
            }
            isLoading={isLoadingRecord}
            boxSx={
              generatedStockDocuments.length || isLoadingRecord
                ? { mt: actionButtonSpacing }
                : undefined
            }
            additionalGenerateProps={
              documentGenerationProps?.additionalGenerateProps
            }
            setSelectedDocumentId={setSelectedDocumentId}
          />
        </StockDocumentsDialog>
      )}
      {record && recordType && (
        <StockDocumentsDialog
          open={isDialogOpen(DETAIL_VIEW_DIALOGS.ATTACHMENTS)}
          onClose={closeDialog}
          selectedDocumentId={selectedDocumentId}
          setSelectedDocumentId={setSelectedDocumentId}
          documents={relatedStockDocuments}
          relatedDocumentRecords={relatedDocumentRecords}
        >
          <InlineUploadDocumentsDropZone
            record={record}
            recordType={recordType}
            size="md"
            setSelectedDocumentId={setSelectedDocumentId}
          />
        </StockDocumentsDialog>
      )}
    </>
  );
};

export default DetailLayout;
